import type { EntityStatus } from '@monorepo/shared/schemas/common'
import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import { Edit, Loader2, Pause, Play } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useUpdateAgent } from '../hooks/useAgents'
import type { Agent } from '../types/agent.types'

interface AgentPreviewCardProps {
  agent?: Agent
  isLoading?: boolean
  onEdit?: () => void
}

export function AgentPreviewCard({
  agent,
  isLoading,
  onEdit,
}: AgentPreviewCardProps) {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const updateAgent = useUpdateAgent()

  const handleStatusToggle = async () => {
    if (!agent) return

    let newStatus: EntityStatus
    if (agent.status === ENTITY_STATUS.draft) {
      newStatus = ENTITY_STATUS.active
    } else {
      newStatus = ENTITY_STATUS.draft // from active to draft
    }

    setIsUpdatingStatus(true)
    try {
      await updateAgent.mutateAsync({
        id: agent.id,
        data: { status: newStatus },
      })
      // React Query will automatically update the cache and trigger re-render
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case ENTITY_STATUS.active:
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case ENTITY_STATUS.draft:
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300'
    }
  }

  const getStatusToggleButton = () => {
    if (!agent) return null

    if (agent.status === ENTITY_STATUS.draft) {
      return (
        <Button
          onClick={handleStatusToggle}
          disabled={isUpdatingStatus}
          size='sm'
          variant='default'
          className='bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 text-white'
        >
          {isUpdatingStatus && (
            <Loader2 className='w-4 h-4 mr-2 animate-spin' />
          )}
          <Play className='w-4 h-4 mr-2' />
          Make Active
        </Button>
      )
    }

    if (agent.status === ENTITY_STATUS.active) {
      return (
        <Button
          onClick={handleStatusToggle}
          disabled={isUpdatingStatus}
          size='sm'
          variant='outline'
          className='border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
        >
          {isUpdatingStatus && (
            <Loader2 className='w-4 h-4 mr-2 animate-spin' />
          )}
          <Pause className='w-4 h-4 mr-2' />
          Make Draft
        </Button>
      )
    }

    return null
  }

  if (isLoading) {
    return (
      <div className='border border-gray-200/60 dark:border-gray-800/60 rounded-lg shadow-sm p-6 space-y-6'>
        {/* Header Skeleton */}
        <div className='flex items-start justify-between'>
          <div className='flex items-center gap-4'>
            <div className='space-y-2'>
              <div className='flex items-center gap-4 flex-wrap'>
                {/* Agent name skeleton */}
                <div className='h-7 w-48 bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>

                {/* Agent ID skeleton */}
                <div className='bg-slate-50 dark:bg-slate-900/50 px-2 py-1 rounded-md'>
                  <div className='h-3 w-16 bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>
                </div>

                {/* External ID skeleton */}
                <div className='bg-slate-50 dark:bg-slate-900/50 px-2 py-1 rounded-md'>
                  <div className='h-3 w-20 bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>
                </div>
              </div>
            </div>
          </div>

          <div className='flex flex-col items-end gap-2'>
            {/* Status badge skeleton */}
            <div className='h-6 w-16 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-full'></div>
            {/* Updated date skeleton */}
            <div className='h-3 w-24 bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>
          </div>
        </div>

        {/* Description skeleton */}
        <div className='px-1 space-y-2'>
          <div className='h-4 w-full bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>
          <div className='h-4 w-3/4 bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>
        </div>

        {/* Action buttons skeleton */}
        <div className='flex items-center justify-between pt-2 border-t border-gray-200/50 dark:border-gray-800/50'>
          <div className='flex items-center gap-3'>
            <div className='h-8 w-24 bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>
            <div className='h-8 w-28 bg-slate-200 dark:bg-slate-700 animate-pulse rounded'></div>
          </div>
        </div>
      </div>
    )
  }

  if (!agent) {
    return (
      <div className='border border-gray-200/60 dark:border-gray-800/60 rounded-lg shadow-sm p-8'>
        <div className='flex items-center justify-center'>
          <span className='text-slate-600 dark:text-slate-400'>
            Agent not found
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className='border border-gray-200/60 dark:border-gray-800/60 rounded-lg shadow-sm p-6 space-y-6'>
      {/* Header Section - Agent Identity */}
      <div className='flex items-start justify-between'>
        <div className='flex items-center gap-4'>
          <div className='space-y-2'>
            <div className='flex items-center gap-4 flex-wrap'>
              <h3
                className='font-semibold text-xl text-slate-900 dark:text-slate-100'
                title={agent.name}
              >
                {agent.name}
              </h3>

              {/* Agent ID inline */}
              <div className='bg-slate-50 dark:bg-slate-900/50 px-2 py-1 rounded-md'>
                <span className='text-xs text-slate-500 dark:text-slate-400 mr-1'>
                  ID:
                </span>
                <code className='text-xs font-mono text-slate-700 dark:text-slate-300'>
                  {agent.id}
                </code>
              </div>

              {/* External ID inline (if exists) */}
              {agent.external_id && (
                <div className='bg-slate-50 dark:bg-slate-900/50 px-2 py-1 rounded-md'>
                  <span className='text-xs text-slate-500 dark:text-slate-400 mr-1'>
                    External ID:
                  </span>
                  <code className='text-xs font-mono text-slate-700 dark:text-slate-300'>
                    {agent.external_id}
                  </code>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='flex flex-col items-end gap-2'>
          <Badge
            className={`${getStatusColor(agent.status)} font-medium capitalize text-xs px-3 py-1`}
          >
            {agent.status}
          </Badge>
          <p className='text-xs text-slate-500 dark:text-slate-400'>
            Updated {new Date(agent.updated_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Description Section */}
      {agent.description && (
        <div className='px-1'>
          <p className='text-sm text-slate-600 dark:text-slate-300 leading-relaxed'>
            {agent.description}
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-2 border-t border-gray-200/50 dark:border-gray-800/50'>
        <div className='flex items-center gap-3'>
          <Button
            variant='outline'
            onClick={onEdit}
            size='sm'
            className='border-gray-300 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-slate-900/50'
          >
            <Edit className='w-4 h-4 mr-2' />
            Edit Details
          </Button>

          {getStatusToggleButton()}
        </div>
      </div>
    </div>
  )
}
